import type { JSONContent } from "@tiptap/core";
import type { EntityCardView } from "@/types/entity";
import {
  isLoreEntryTypeImageGenerationEligible,
  type LoreEntryView,
} from "@/types/lore";
import type {
  ImageSourceCitation,
  ProjectArtstyleReferenceView,
} from "@/features/image-generation/imageGeneration.types";

function flattenText(content: JSONContent | null | undefined): string {
  if (!content) {
    return "";
  }

  if (typeof content.text === "string") {
    return content.text;
  }

  return (content.content ?? []).map((node) => flattenText(node)).join(" ");
}

function normalizeWhitespace(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

function appendDetail(
  details: string[],
  value: string | null | undefined,
  label?: string,
) {
  const normalized = normalizeWhitespace(value ?? "");
  if (!normalized) {
    return;
  }

  details.push(label ? `${label}: ${normalized}` : normalized);
}

export function buildArtDirectionSummary(
  styleDescription: string | null | undefined,
  references: ProjectArtstyleReferenceView[],
) {
  const normalizedStyleDescription = normalizeWhitespace(styleDescription ?? "");
  if (normalizedStyleDescription) {
    return normalizedStyleDescription;
  }

  const fallbackLabels = references
    .map((reference) => reference.fileName.trim())
    .filter(Boolean)
    .slice(0, 5);

  if (fallbackLabels.length === 0) {
    return "";
  }

  return `Use the shared art direction from ${fallbackLabels.join(", ")}.`;
}

export function buildArtstyleAnchorCitations(references: ProjectArtstyleReferenceView[]) {
  return references.map<ImageSourceCitation>((reference) => ({
    canonStatusLabel: null,
    reason: "Shared project art direction reference.",
    sourceKind: "artstyle_reference",
    sourceLabel: reference.fileName,
    sourceRecordId: null,
  }));
}

export function buildEntityImageDescriptionParts(entity: EntityCardView) {
  const details: string[] = [];

  switch (entity.type) {
    case "character":
      appendDetail(details, entity.aliases.join(", "), "Aliases");
      appendDetail(details, entity.ageText, "Age");
      appendDetail(details, entity.raceLink?.targetTitle ?? null, "Race or species");
      appendDetail(details, entity.physicalDescription, "Physical description");
      appendDetail(details, entity.personalityTraits, "Personality");
      appendDetail(details, entity.skillsAndAbilities, "Skills and abilities");
      appendDetail(details, entity.notes, "Notes");
      break;
    case "item-object":
      appendDetail(details, entity.itemCategory, "Category");
      appendDetail(details, entity.itemDescription, "Description");
      appendDetail(details, entity.origin, "Origin");
      appendDetail(details, entity.currentHolderLink?.targetTitle ?? null, "Current holder");
      break;
    case "creature":
      appendDetail(details, entity.speciesLink?.targetTitle ?? null, "Species");
      appendDetail(details, entity.creatureDescription, "Description");
      appendDetail(details, entity.creatureAbilities, "Abilities");
      break;
    case "location":
      appendDetail(details, entity.locationDescription, "Description");
      appendDetail(details, entity.locationNotes, "Notes");
      break;
    case "machine":
      appendDetail(details, entity.machineDescription, "Description");
      appendDetail(details, entity.machineCapabilities, "Capabilities");
      appendDetail(details, entity.origin, "Origin");
      appendDetail(details, entity.currentHolderLink?.targetTitle ?? null, "Current holder");
      break;
  }

  return details;
}

export function isLoreEntryImageGenerationEligible(
  entry: Pick<LoreEntryView, "type">,
) {
  return isLoreEntryTypeImageGenerationEligible(entry.type);
}

export function buildEntityImagePrompt(input: {
  descriptionParts: string[];
  entityName: string;
  styleDescription: string | null | undefined;
  typeLabel: string;
}) {
  const details = input.descriptionParts
    .map((part) => normalizeWhitespace(part))
    .filter(Boolean)
    .join(". ");
  const prompt = [
    `Create a ${input.typeLabel.toLowerCase()} illustration of ${input.entityName}.`,
    details,
    buildArtDirectionSummary(input.styleDescription, []),
  ]
    .map((segment) => normalizeWhitespace(segment))
    .filter(Boolean)
    .join(" ");

  return prompt.trim();
}

export function extractStoryExcerpt(
  body: JSONContent | null | undefined,
  maxLength = 280,
) {
  const normalized = normalizeWhitespace(flattenText(body));
  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`;
}

export function buildChapterImagePrompt(input: {
  chapterBody: JSONContent | null | undefined;
  chapterTitle: string;
  styleDescription: string | null | undefined;
}) {
  const excerpt = extractStoryExcerpt(input.chapterBody);

  return [
    `Create a chapter illustration for "${input.chapterTitle}".`,
    excerpt ? `Base it on this chapter excerpt: ${excerpt}` : "",
    buildArtDirectionSummary(input.styleDescription, []),
  ]
    .map((segment) => normalizeWhitespace(segment))
    .filter(Boolean)
    .join(" ");
}

export function buildSideStoryImagePrompt(input: {
  body: JSONContent | null | undefined;
  styleDescription: string | null | undefined;
  summary: string | null | undefined;
  title: string;
}) {
  const excerpt = extractStoryExcerpt(input.body, 320);
  const summary = normalizeWhitespace(input.summary ?? "");

  return [
    `Create a side story illustration for "${input.title}".`,
    summary ? `Use this side story summary: ${summary}` : "",
    excerpt ? `Base it on this side story excerpt: ${excerpt}` : "",
    buildArtDirectionSummary(input.styleDescription, []),
  ]
    .map((segment) => normalizeWhitespace(segment))
    .filter(Boolean)
    .join(" ");
}

export function buildLoreImagePrompt(input: {
  body: JSONContent | null | undefined;
  styleDescription: string | null | undefined;
  title: string;
  typeLabel: string;
}) {
  const description = extractStoryExcerpt(input.body, 320);

  return [
    `Create a ${input.typeLabel.toLowerCase()} illustration for ${input.title}.`,
    description,
    buildArtDirectionSummary(input.styleDescription, []),
  ]
    .map((segment) => normalizeWhitespace(segment))
    .filter(Boolean)
    .join(" ");
}

export function buildTimelineEventImagePrompt(input: {
  description: string | null | undefined;
  eraLabel: string | null | undefined;
  styleDescription: string | null | undefined;
  title: string;
}) {
  const description = normalizeWhitespace(input.description ?? "");
  const eraLabel = normalizeWhitespace(input.eraLabel ?? "");

  return [
    `Create a timeline event illustration for "${input.title}".`,
    eraLabel ? `Era or date label: ${eraLabel}.` : "",
    description ? `Base it on this event summary: ${description}` : "",
    buildArtDirectionSummary(input.styleDescription, []),
  ]
    .map((segment) => normalizeWhitespace(segment))
    .filter(Boolean)
    .join(" ");
}
